// Translations. The dictionaries live in i18n/<code>.json — adding a language
// means dropping a file there and listing it in LANGUAGES below.
//
// The current language is module state, not React state: socket handlers and
// alerts registered once at mount would otherwise keep translating with the
// language that was active when they were registered. Components re-render
// through useLanguage(), which subscribes to language changes.

import React, { useEffect, useState } from "react";

import fr from "./i18n/fr.json";
import en from "./i18n/en.json";

const DICTS = { fr, en };

// Order shown in the language picker
export const LANGUAGES = [
  { code: "fr", label: "Français" },
  { code: "en", label: "English" },
];

// Language of the reference dictionary: used whenever a key is missing
// elsewhere, so a partially translated file still renders something.
const FALLBACK = "fr";
const STORAGE_KEY = "popuquiz_lang";

// Explicit choice first, then the browser/OS preferences (navigator.languages
// is ordered by preference), then the reference language.
export function detectLanguage() {
  const saved = localStorage.getItem(STORAGE_KEY);
  if (saved && DICTS[saved]) return saved;
  const wanted = navigator.languages?.length ? navigator.languages : [navigator.language];
  for (const tag of wanted || []) {
    const code = String(tag).toLowerCase().split("-")[0];
    if (DICTS[code]) return code;
  }
  return FALLBACK;
}

let currentLang = detectLanguage();
document.documentElement.lang = currentLang;

const listeners = new Set();

export function getLanguage() {
  return currentLang;
}

export function setLanguage(code) {
  if (!DICTS[code] || code === currentLang) return;
  currentLang = code;
  localStorage.setItem(STORAGE_KEY, code);
  document.documentElement.lang = code;
  listeners.forEach(fn => fn(code));
}

// Locale tag for Intl / toLocaleString (dates, numbers)
export function locale() {
  return currentLang === "fr" ? "fr-FR" : "en-GB";
}

function lookup(dict, key) {
  return key.split(".").reduce((o, k) => (o == null ? undefined : o[k]), dict);
}

// Plural: a key may hold { one, other } instead of a string. French counts 0
// as singular ("0 point"), English does not ("0 points").
function plural(forms, count) {
  const one = currentLang === "fr" ? count < 2 : count === 1;
  return (one ? forms.one : forms.other) ?? forms.other;
}

function resolve(key, vars) {
  let raw = lookup(DICTS[currentLang], key);
  if (raw === undefined) raw = lookup(DICTS[FALLBACK], key);
  if (raw === undefined) return key; // missing key: show it rather than nothing
  if (raw && typeof raw === "object") raw = plural(raw, Number(vars?.count) || 0);
  return raw;
}

// t("lobby.players", { count: 3 }) → "Joueurs · 3"
export function t(key, vars) {
  const raw = resolve(key, vars);
  if (!vars) return raw;
  return String(raw).replace(/\{(\w+)\}/g, (m, name) =>
    vars[name] === undefined ? m : String(vars[name]));
}

// Same, for sentences that embed React nodes:
//   tx("finalScores.tieBreakWinner", { name: <b>{pseudo}</b> })
// Returns an array of nodes, usable directly inside JSX.
export function tx(key, vars) {
  const raw = String(resolve(key, vars));
  const out = [];
  let last = 0;
  const re = /\{(\w+)\}/g;
  let m;
  while ((m = re.exec(raw)) !== null) {
    if (m.index > last) out.push(raw.slice(last, m.index));
    const value = vars?.[m[1]];
    out.push(value === undefined ? m[0] : value);
    last = m.index + m[0].length;
  }
  if (last < raw.length) out.push(raw.slice(last));
  return out.map((node, i) =>
    typeof node === "string" ? node : <React.Fragment key={i}>{node}</React.Fragment>);
}

// Ordinal used in rankings: 1er / 2e — 1st / 2nd / 3rd / 4th
export function ordinal(n) {
  if (currentLang === "fr") return n === 1 ? "1er" : `${n}e`;
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${n}th`;
  return `${n}${["th", "st", "nd", "rd"][n % 10] || "th"}`;
}

// Subscribes a component to language changes. One call high in the tree is
// enough to re-render everything below it (no component is memoised).
export function useLanguage() {
  const [lang, setLang] = useState(currentLang);
  useEffect(() => {
    listeners.add(setLang);
    return () => listeners.delete(setLang);
  }, []);
  return lang;
}
